import React from 'react';
import SEO from '../../components/SEO/SEO';

import { Link } from 'react-router-dom';
import './GoHighLevel.css';

const GoHighLevel = () => {
  const ghlServices = [
    {
      id: 'email-marketing',
      icon: '📧',
      title: 'Email Marketing',
      description: 'Powerful email marketing automation with segmentation, personalization, and advanced analytics.',
      features: [
        'Automated Email Sequences',
        'A/B Testing & Optimization',
        'Advanced Segmentation',
        'Behavioral Triggers'
      ]
    },
    {
      id: 'sms-marketing',
      icon: '💬',
      title: 'SMS Marketing',
      description: 'Reach customers instantly with SMS campaigns and automated text messages.',
      features: [
        'SMS Campaigns',
        'Text Message Automation',
        'Two-Way Conversations',
        'SMS Surveys'
      ]
    },
    {
      id: 'crm',
      icon: '👥',
      title: 'CRM & Lead Management',
      description: 'Complete customer relationship management with pipeline tracking and automation.',
      features: [
        'Contact Management',
        'Sales Pipeline Tracking',
        'Lead Scoring',
        'Deal Management'
      ]
    },
    {
      id: 'analytics',
      icon: '📊',
      title: 'Analytics & Reporting',
      description: 'Comprehensive analytics and reporting to track performance across all channels.',
      features: [
        'Real-time Dashboard',
        'Revenue Tracking',
        'Campaign Performance',
        'Custom Reports'
      ]
    },
    {
      id: 'automation',
      icon: '🤖',
      title: 'Marketing Automation',
      description: 'Build complex automation workflows to nurture leads and engage customers.',
      features: [
        'Workflow Automation',
        'Multi-Channel Campaigns',
        'Conditional Logic',
        'Event Triggers'
      ]
    },
    {
      id: 'appointments',
      icon: '📅',
      title: 'Appointment Scheduling',
      description: 'Automated appointment booking with calendar integration and reminders.',
      features: [
        'Online Booking',
        'Calendar Sync',
        'Automated Reminders',
        'Time Zone Management'
      ]
    },
    {
      id: 'funnels',
      icon: '🎯',
      title: 'Sales Funnels',
      description: 'Build high-converting sales funnels with landing pages and checkout.',
      features: [
        'Drag-and-Drop Builder',
        'Landing Page Templates',
        'Checkout & Payments',
        'Funnel Analytics'
      ]
    },
    {
      id: 'reputation',
      icon: '⭐',
      title: 'Reputation Management',
      description: 'Manage online reviews and build your business reputation.',
      features: [
        'Review Generation',
        'Review Monitoring',
        'Review Response',
        'Review Widgets'
      ]
    }
  ];

  return (
    <div className="ghl-page">
            <SEO 
                title="Go High Level Services | Digi Network" 
                description="Learn more about our Go High Level services to grow your business online with Digi Network." 
                canonical="/gohighlevel/gohighlevel" 
            />

      <section className="ghl-hero">
        <div className="ghl-hero-bg"></div>
        <div className="container">
          <div className="ghl-hero-content">
            <div className="label">GOHIGHLEVEL PLATFORM</div>
            <h1>All-in-One Marketing Platform</h1>
            <p className="ghl-hero-description">
              GoHighLevel is the complete marketing and sales platform that combines email marketing, 
              SMS, CRM, automation, analytics, and more in one powerful system. We help you set up, 
              optimize, and manage your entire marketing stack.
            </p>
            <div className="ghl-hero-cta">
              <Link to="/contact" className="btn btn-primary">
                Get Started
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
              <Link to="/case-studies" className="btn btn-outline">View Results</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="ghl-stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">10,000+</div>
              <div className="stat-label">Active Users</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">50M+</div>
              <div className="stat-label">Emails Sent Monthly</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">95%</div>
              <div className="stat-label">Customer Satisfaction</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">$50M+</div>
              <div className="stat-label">Revenue Generated</div>
            </div>
          </div>
        </div>
      </section>

      <section className="ghl-services-grid-section">
        <div className="container">
          <div className="section-header">
            <div className="label">GOHIGHLEVEL SERVICES</div>
            <h2>Complete Marketing & Sales Platform</h2>
            <p>We provide full GoHighLevel setup, training, and management services.</p>
          </div>

          <div className="ghl-services-grid">
            {ghlServices.map((service) => (
              <div key={service.id} className="ghl-service-card">
                <div className="ghl-service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p className="ghl-service-description">{service.description}</p>

                <ul className="ghl-service-features">
                  {service.features.map((feature, index) => (
                    <li key={index}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="10" fill="#0037FC" opacity="0.1"/>
                        <path d="M6 10L9 13L14 7" stroke="#0037FC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ghl-why">
        <div className="container">
          <div className="section-header">
            <div className="label">WHY GOHIGHLEVEL</div>
            <h2>Why Choose GoHighLevel?</h2>
          </div>

          <div className="why-grid">
            <div className="why-item">
              <div className="why-icon">💰</div>
              <h3>Cost Effective</h3>
              <p>Replace multiple tools with one platform. Save thousands on software subscriptions.</p>
            </div>

            <div className="why-item">
              <div className="why-icon">🔗</div>
              <h3>All-in-One</h3>
              <p>Email, SMS, CRM, automation, funnels, and more in a single integrated platform.</p>
            </div>

            <div className="why-item">
              <div className="why-icon">⚡</div>
              <h3>Powerful Automation</h3>
              <p>Build complex workflows with conditional logic, triggers, and multi-channel campaigns.</p>
            </div>

            <div className="why-item">
              <div className="why-icon">📊</div>
              <h3>Advanced Analytics</h3>
              <p>Track performance across all channels with comprehensive reporting and insights.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="ghl-process">
        <div className="container">
          <div className="section-header">
            <div className="label">OUR PROCESS</div>
            <h2>How We Set Up GoHighLevel</h2>
          </div>

          <div className="process-grid">
            <div className="process-step">
              <div className="process-number">01</div>
              <h3>Account Setup</h3>
              <p>Configure your GoHighLevel account with proper settings, branding, and integrations.</p>
            </div>

            <div className="process-step">
              <div className="process-number">02</div>
              <h3>Customization</h3>
              <p>Customize funnels, templates, automations, and workflows for your business.</p>
            </div>

            <div className="process-step">
              <div className="process-number">03</div>
              <h3>Integration</h3>
              <p>Connect your existing tools and import your data into the platform.</p>
            </div>

            <div className="process-step">
              <div className="process-number">04</div>
              <h3>Training & Support</h3>
              <p>Train your team and provide ongoing support to maximize platform usage.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="ghl-cta">
        <div className="container">
          <div className="ghl-cta-content">
            <h2>Ready to Streamline Your Marketing?</h2>
            <p>Let's set up GoHighLevel and transform your marketing operations.</p>
            <Link to="/contact" className="btn btn-primary">
              Get Started with GoHighLevel
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GoHighLevel;
